import { useEffect } from 'react';
import i18n from '../i18n';
import { getAmplifyVisitorRegion, type VisitorRegion } from '../utils/amplifyGeo';
import { getLanguageFromPath } from '../utils/languageRouting';

const MANUAL_LANGUAGE_STORAGE_KEY = 'jlbhk_manual_language_v1';
const AUTO_REGION_LANGUAGE_STORAGE_KEY = 'jlbhk_auto_region_language_v1';
const I18NEXT_LANGUAGE_STORAGE_KEY = 'i18nextLng';
const FALLBACK_LANGUAGE = 'hk';

function normalizeLanguage(lng?: string | null) {
  if (!lng) return FALLBACK_LANGUAGE;

  const language = lng.toLowerCase().replace('_', '-');

  if (language === 'vn' || language.startsWith('vi')) return 'vn';
  if (language.startsWith('zh-tw')) return 'hk';
  if (language.startsWith('zh-hk')) return 'hk';
  if (language.startsWith('zh-cn')) return 'cn';
  if (language.startsWith('zh-hans')) return 'cn';
  if (language.startsWith('zh')) return 'hk';
  if (language.startsWith('ja')) return 'jp';
  if (language.startsWith('ko')) return 'kr';
  if (language.startsWith('th')) return 'th';
  if (language.startsWith('de')) return 'de';
  if (language.startsWith('fr')) return 'fr';
  if (language.startsWith('it')) return 'it';
  if (language.startsWith('es')) return 'es';
  if (language.startsWith('ug')) return 'ug';
  if (language.startsWith('en')) return 'en';

  return FALLBACK_LANGUAGE;
}

function getNavigatorLanguage() {
  if (typeof navigator === 'undefined') return FALLBACK_LANGUAGE;

  return normalizeLanguage(navigator.languages?.[0] || navigator.language);
}

function getRegionLanguage(region: VisitorRegion) {
  const normalizedCountryCode = region.countryCode?.trim().toUpperCase();

  if (normalizedCountryCode === 'HK') return 'hk';
  if (normalizedCountryCode === 'CN' && region.isXinjiang) return 'ug';
  if (normalizedCountryCode === 'CN') return 'cn';

  return null;
}

function safeGetLocalStorage(key: string) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetLocalStorage(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore blocked storage, for example private browsing mode.
  }
}

function safeRemoveLocalStorage(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore blocked storage, for example private browsing mode.
  }
}

export function markManualLanguageSelection() {
  if (typeof window === 'undefined') return;

  safeSetLocalStorage(MANUAL_LANGUAGE_STORAGE_KEY, '1');
  safeRemoveLocalStorage(AUTO_REGION_LANGUAGE_STORAGE_KEY);
}

export function useRegionLanguage() {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const routeLanguage = getLanguageFromPath(window.location.pathname, __BASE_PATH__);

    if (routeLanguage) {
      if (i18n.language !== routeLanguage) {
        i18n.changeLanguage(routeLanguage);
      }

      safeSetLocalStorage(I18NEXT_LANGUAGE_STORAGE_KEY, routeLanguage);
      safeRemoveLocalStorage(AUTO_REGION_LANGUAGE_STORAGE_KEY);
      return undefined;
    }

    let active = true;

    getAmplifyVisitorRegion()
      .then((region) => {
        if (!active) return;

        const manualLanguageSelected = safeGetLocalStorage(MANUAL_LANGUAGE_STORAGE_KEY) === '1';
        if (manualLanguageSelected) return;

        const regionLanguage = getRegionLanguage(region);
        const previousAutoLanguage = safeGetLocalStorage(AUTO_REGION_LANGUAGE_STORAGE_KEY);
        const currentStoredLanguage = safeGetLocalStorage(I18NEXT_LANGUAGE_STORAGE_KEY);

        if (regionLanguage) {
          if (i18n.language !== regionLanguage) {
            i18n.changeLanguage(regionLanguage);
          }

          safeSetLocalStorage(AUTO_REGION_LANGUAGE_STORAGE_KEY, regionLanguage);

          if (typeof console !== 'undefined') {
            console.info('[JL金利寶] language matched by region:', {
              countryCode: region.countryCode,
              regionCode: region.regionCode,
              isXinjiang: region.isXinjiang,
              language: regionLanguage,
              source: region.source,
            });
          }

          return;
        }

        // For OTHER / unknown regions, do not force HK/CN. If the current language came from a
        // previous automatic region match, restore normal browser-language behavior.
        if (previousAutoLanguage && currentStoredLanguage === previousAutoLanguage) {
          const browserLanguage = getNavigatorLanguage();
          safeRemoveLocalStorage(I18NEXT_LANGUAGE_STORAGE_KEY);
          safeRemoveLocalStorage(AUTO_REGION_LANGUAGE_STORAGE_KEY);
          i18n.changeLanguage(browserLanguage);

          if (typeof console !== 'undefined') {
            console.info('[JL金利寶] language restored from browser preference:', {
              countryCode: region.countryCode,
              language: browserLanguage,
              source: region.source,
            });
          }
        }
      })
      .catch((error) => {
        if (typeof console !== 'undefined') {
          console.warn('[JL金利寶] region language match failed:', error);
        }
      });

    return () => {
      active = false;
    };
  }, []);
}
